/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/display-name */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Box, Fade } from '@material-ui/core';
import useConsoleConverTags from '@src/hooks/useConsoleConverTags';
import {
  chatHistoryActions,
  chatHistoryInitialState,
  ChatHistoryState,
} from '@src/reducers/chatHistory';
import { selectChatHistory } from '@src/selectors/chatHistory';
import { activeChatSelector } from '@src/selectors/chats';
import { showDepartmentList } from '@src/selectors/chatViewer';
import { ChatTray } from '@src/types/chats';
import useDebouncedValue from '@src/utils/useDebouncedValue';
import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CONVER_TAGS_SCROLL_OFFSET } from '../AgentConsole/Layout/Utils';
import { ActionsAI } from '../ChatMessageComposer/Utils';
import DotsProgress from '../UI/DotsProgress';
import {
  ChatHistoryContext,
  useChatHistoryContextValue,
} from './ChatHistoryContext';
import ChatHistoryItem from './ChatHistoryItem';
import './ChatMessage/ChatMessage.styles.css';
import { DepartmentSlide } from './ChatMessage/ListTypeMessage';
import './ChatMessageActions/ChatMessageActions.style.css';
import ScrollToBottomButton from './ScrollToBottomButton';
import './Timeline.css';

export type Context = ChatHistoryState[string] & {
  openImageViewer(src: string): void;
  autoscrollToBottom(): void;
};

type ScrollTimeoutRef = NodeJS.Timeout | null;

const ChatHistory: React.FC = () => {
  const dispatch = useDispatch();

  const { id: referenceChatId, contact } = useSelector(
    activeChatSelector
  ) as ChatTray;

  const chatHistoryState =
    useSelector(selectChatHistory)[referenceChatId] || chatHistoryInitialState;
  const isOpenList = useSelector(showDepartmentList);
  const { isConsoleConverTagsVisible } = useConsoleConverTags();

  const { loading, firstItemIndex, completed, history } = chatHistoryState;
  const [historyDebounced] = useDebouncedValue(history, 100);

  useEffect(() => {
    dispatch(chatHistoryActions.fetchEvents({ referenceChatId }));
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceChatId]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [lastScrollPostion, setLastScrollPosition] = useState(0);
  const [lastScrollHeight, setLastScrollHeight] = useState(0);
  const [isAwaitingResponse, setAwaitingResponse] = useState(false);
  const [existsScroll, setExistsScroll] = useState(false);

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ScrollTimeoutRef>(null);

  const [atBottom, setAtBottom] = useState(false);
  const threshold = 300;

  useEffect(() => {
    // prettier-ignore
    const isAddedMoreMsgs = history.length > 0 && history.length > historyDebounced.length;
    if (containerRef.current && isAddedMoreMsgs) {
      // prettier-ignore
      const isChangedActiveChat = historyDebounced[0]?.chat?.id !== history[0]?.chat?.id;
      const isAddedOnlyOneMsg = history.length - historyDebounced.length > 1;

      if (atBottom || !historyDebounced?.length || isChangedActiveChat) {
        scrollToBottom();
      } else if (isAddedOnlyOneMsg) {
        setAwaitingResponse(true);
        const currScrollHeight = containerRef.current.scrollHeight;
        // prettier-ignore
        const scrollPostion = (currScrollHeight - lastScrollHeight) + lastScrollPostion;
        containerRef.current.scrollTo(0, scrollPostion);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.length]);

  useEffect(() => {
    if (containerRef?.current?.firstChild) {
      const contentChild = containerRef.current.firstChild as HTMLElement;
      setExistsScroll(
        contentChild?.clientHeight > containerRef.current.clientHeight
      );
    }
  }, [containerRef, history.length]);

  useEffect(() => {
    if (!containerRef.current || !existsScroll) return;

    const resizeObserver = new ResizeObserver(() => {
      if (isConsoleConverTagsVisible()) {
        containerRef?.current?.scrollTo(
          0,
          containerRef.current.scrollTop + CONVER_TAGS_SCROLL_OFFSET
        );
      }
    });

    resizeObserver.observe(containerRef.current);

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existsScroll]);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const el = event.target as HTMLElement;
    const scrollTop = el?.scrollTop;
    const scrollBottom = el?.scrollTop + el?.clientHeight;

    setIsScrolling(true);
    setAtBottom(el.scrollHeight - scrollBottom <= 200);

    if (scrollTimeoutRef?.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    if (scrollTop <= threshold && !completed && !loading) {
      setAwaitingResponse(false);
      if (!isAwaitingResponse) {
        setLastScrollPosition(threshold + 1);
        setLastScrollHeight(el?.scrollHeight || 0);
        dispatch(chatHistoryActions.fetchEvents({ referenceChatId }));
      }
    } else {
      event.preventDefault();
    }

    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 400);
  };

  const scrollToBottom = () => {
    containerRef?.current?.scrollTo(0, containerRef.current.scrollHeight);
  };

  const contextValue = useChatHistoryContextValue();

  return (
    <ChatHistoryContext.Provider value={contextValue}>
      <Fade
        key={referenceChatId}
        in
        mountOnEnter
        unmountOnExit
        style={{ pointerEvents: isOpenList ? 'none' : 'initial' }}
      >
        <div
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            flexGrow: 1,
            paddingTop: 10,
            padding: '0 10px',
            overflowY: 'auto',
            position: 'relative',
          }}
          id="chat-container"
          data-testid="chat-history"
        >
          <InfiniteScroll
            loadMore={Function.call}
            loader={
              <>
                {!completed && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={32}
                  >
                    {loading && <DotsProgress size="xs" />}
                  </Box>
                )}
              </>
            }
          >
            {history.map((_, index) => {
              const reverseIndex = history.length - index - 1;
              return (
                <ChatHistoryItem
                  key={history[reverseIndex].itemIndex}
                  next={history.at(reverseIndex - 1)}
                  prev={history.at(reverseIndex + 1)}
                  current={history[reverseIndex]}
                  firstItemIndex={firstItemIndex}
                  contact={contact}
                  completed={completed}
                  isScrolling={isScrolling}
                />
              );
            })}
          </InfiniteScroll>
          <ScrollToBottomButton
            visible={!atBottom && existsScroll && !!history?.length}
            onClick={scrollToBottom}
          />
        </div>
      </Fade>
      <ActionsAI />
      <DepartmentSlide customClasses="ds-console" />
      <div
        id="snackbar-subcontainer"
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
      />
    </ChatHistoryContext.Provider>
  );
};

export default injectIntl(ChatHistory);
